import React, { useEffect, useState } from 'react';
import { Building, Home, Activity, FileText } from 'lucide-react';
import api from '../../services/api';
import { urlsServices } from '../../configs/urlsConfigAppBase';

interface Projeto {
    id: number;
    nome: string;
    status?: string;
    created_at: string;
    orcamento?: {
        pdf: string;
    };
}

const ClientDashboard: React.FC = () => {

    const [projetos, setProjetos] = useState<Projeto[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchProjetos = async () => {
            try {
                setLoading(true);
                const response = await api.get('/projetos/cliente');
                // A API retorna { projeto: [...] }
                setProjetos(response.data.projeto || []);
            } catch (error) {
                console.error("Erro ao buscar projetos:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchProjetos();
    }, []);

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR');
    };

    const handleViewBudget = (pdfPath: string) => {
        // Se já for uma URL completa, abre direto
        if (pdfPath.startsWith('http')) {
            window.open(pdfPath, '_blank');
            return;
        }

        // Se for um caminho absoluto do servidor (ex: /var/www/html/...), extraímos o nome do arquivo
        if (pdfPath.startsWith('/')) {
            const fileName = pdfPath.split('/').pop();
            const baseUrl = urlsServices.BACKENDWS.replace(/\/api\/?$/, '');
            window.open(`${baseUrl}/storage/orcamentos/${fileName}`, '_blank');
            return;
        }

        // Se for o novo formato (caminho relativo)
        const baseUrl = urlsServices.BACKENDWS.replace(/\/api\/?$/, '');
        window.open(`${baseUrl}/storage/${pdfPath}`, '_blank');
    };

    const getStatusBadge = (status?: string) => {
        const s = status?.toLowerCase() || 'em progresso';
        switch (s) {
            case 'aprovado':
                return <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/50 dark:text-green-400">Aprovado</span>;
            case 'em análise':
            case 'em analise':
                return <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-400">Em análise</span>;
            case 'planejamento':
                return <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/50 dark:text-blue-400">Planejamento</span>;
            default:
                return <span className="inline-flex items-center rounded-full bg-orange-100 px-2.5 py-0.5 text-xs font-medium text-orange-800 dark:bg-orange-900/50 dark:text-orange-400">Em progresso</span>;
        }
    };

    return (
        <>
            <div className="flex flex-wrap justify-between gap-3 mb-8">
                <div className="flex flex-col gap-1">
                    <p className="text-slate-900 dark:text-white text-3xl font-bold leading-tight tracking-[-0.02em]">Dashboard do Cliente</p>
                    <p className="text-slate-500 dark:text-slate-400 text-base font-normal leading-normal">Visão geral dos seus projetos e orçamentos.</p>
                </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                <div className="lg:col-span-2 flex flex-col gap-6 rounded-xl p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
                    <h2 className="text-slate-900 dark:text-white text-xl font-bold leading-tight tracking-[-0.015em]">Progresso dos Orçamentos</h2>
                    <div className="h-64 flex items-center justify-center bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-dashed border-slate-300 dark:border-slate-700">
                        <p className="text-slate-500 dark:text-slate-400">Gráfico de Progresso (Placeholder)</p>
                    </div>
                </div>
                <div className="flex flex-col gap-4 rounded-xl p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
                    <h2 className="text-slate-900 dark:text-white text-xl font-bold leading-tight tracking-[-0.015em]">Projetos Recentes</h2>
                    <div className="flex flex-col gap-4">
                        {loading ? (
                            <p className="text-sm text-slate-500">Carregando...</p>
                        ) : projetos.length > 0 ? (
                            projetos.slice(0, 3).map((projeto, index) => (
                                <div key={projeto.id} className="flex items-center gap-3">
                                    <div className="flex size-10 items-center justify-center rounded-lg bg-[#137fec]/10 dark:bg-[#137fec]/20">
                                        {index % 3 === 0 ? <Building className="text-[#137fec] size-5" /> :
                                            index % 3 === 1 ? <Home className="text-[#137fec] size-5" /> :
                                                <Activity className="text-[#137fec] size-5" />}
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center justify-between">
                                            <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{projeto.nome}</p>
                                            {projeto.orcamento?.pdf && (
                                                <button
                                                    onClick={() => handleViewBudget(projeto.orcamento!.pdf)}
                                                    className="p-1 text-[#137fec] hover:bg-slate-100 dark:hover:bg-slate-800 rounded-md transition-colors"
                                                    title="Ver Orçamento"
                                                >
                                                    <FileText size={16} />
                                                </button>
                                            )}
                                        </div>
                                        <p className="text-xs text-slate-500 dark:text-slate-400">{projeto.status || 'Em progresso'}</p>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p className="text-sm text-slate-500">Nenhum projeto encontrado.</p>
                        )}
                    </div>
                </div>
            </div>
            <div className="flex flex-col gap-4 rounded-xl p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
                <h2 className="text-slate-900 dark:text-white text-xl font-bold leading-tight tracking-[-0.015em]">Projetos em Andamento</h2>
                <div className="overflow-x-auto">
                    <table className="min-w-full text-left text-sm">
                        <thead>
                            <tr className="border-b border-slate-200 dark:border-slate-800">
                                <th className="px-4 py-3 font-medium text-slate-500 dark:text-slate-400" scope="col">Nome do Projeto</th>
                                <th className="px-4 py-3 font-medium text-slate-500 dark:text-slate-400" scope="col">Status</th>
                                <th className="px-4 py-3 font-medium text-slate-500 dark:text-slate-400" scope="col">Data de Criação</th>
                                <th className="px-4 py-3 font-medium text-slate-500 dark:text-slate-400 text-right" scope="col">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={4} className="px-4 py-8 text-center text-slate-500">Carregando projetos...</td>
                                </tr>
                            ) : projetos.length > 0 ? (
                                projetos.map((projeto) => (
                                    <tr key={projeto.id} className="border-b border-slate-100 dark:border-slate-800">
                                        <td className="px-4 py-4 font-medium text-slate-800 dark:text-slate-200">{projeto.nome}</td>
                                        <td className="px-4 py-4">
                                            {getStatusBadge(projeto.status)}
                                        </td>
                                        <td className="px-4 py-4 text-slate-500 dark:text-slate-400">{formatDate(projeto.created_at)}</td>
                                        <td className="px-4 py-4 text-right">
                                            <div className="flex justify-end gap-2">
                                                {projeto.orcamento?.pdf && (
                                                    <button
                                                        onClick={() => handleViewBudget(projeto.orcamento!.pdf)}
                                                        className="flex h-8 items-center justify-center gap-x-2 rounded-lg border border-[#137fec] bg-[#137fec]/10 px-3 text-sm font-bold text-[#137fec] hover:bg-[#137fec]/20 transition-colors"
                                                    >
                                                        <FileText size={14} />
                                                        Ver Orçamento
                                                    </button>
                                                )}
                                                <button className="flex h-8 items-center justify-center gap-x-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-bold text-slate-700 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 transition-colors">
                                                    Ver detalhes
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={4} className="px-4 py-8 text-center text-slate-500">Nenhum projeto em andamento.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
};

export default ClientDashboard;
